import { TeamRoleOption } from "@rogoag/airtable/crm";
import { UserSettings } from "@domain/Settings";
/**
 * Proagrica OAuth token shape stored on the user. Owned by the user domain
 * because it lives on the User entity; the infrastructure layer re-exports it
 * for consumers that interact with the Proagrica API directly.
 */
export interface ProagricaAuth {
	access_token: string;
	expires_in: number;
	id_token: string;
	refresh_token: string;
	scope: string;
	token_type: string;
}

export type Role = TeamRoleOption | "Customer";

export interface IUser {
	id: string;
	name: string;
	token: string;
	username: string;
	email: string;
	fullName: string;
	disabled: boolean;
	companyIds: string[];
	companyIdsAll: string[];
	portalSettings: UserSettings;
	portalImportOptions: string[];
	roles: Role[];
	proagricaAuth?: ProagricaAuth;
}

export class User implements IUser {
	constructor(
		public id: string,
		public name: string,
		public token: string,
		public username: string,
		public email: string,
		public fullName: string,
		public disabled: boolean,
		public companyIds: string[],
		public companyIdsAll: string[],
		public portalSettings: UserSettings,
		public portalImportOptions: string[],
		public roles: Role[],
		public proagricaAuth?: ProagricaAuth,
	) {}

	static fromIUser(data: IUser, token?: string): User {
		return new User(
			data.id,
			data.name,
			token ?? data.token,
			data.username,
			data.email,
			data.fullName,
			data.disabled,
			data.companyIds,
			data.companyIdsAll,
			data.portalSettings,
			data.portalImportOptions,
			data.roles,
			data.proagricaAuth,
		);
	}

	get isDeveloper() {
		return this.roles.includes("Developer");
	}

	get isSoftwareTester() {
		return this.roles.includes("Software Tester") || this.isDeveloper;
	}

	get isAdmin() {
		return this.roles.includes("App admin") || this.isDeveloper;
	}

	/** Rogo staff, by email domain — mirrors the backend `is_rogo_user` (excludes `+` test accounts).
	 * Ops actions (portal cache clear) are gated to this population server-side. */
	get isRogo() {
		return this.email.endsWith("@rogoag.com") && !this.email.includes("+");
	}

	get isOfficePersonnel() {
		return this.roles.includes("Office") || this.isAdmin;
	}

	get isMapmaker() {
		return this.roles.includes("Mapmaker") || this.isAdmin;
	}

	get isCustomer() {
		return this.roles.includes("Customer") || this.isAdmin;
	}

	get isSalesperson() {
		return this.roles.includes("Sales") || this.isAdmin;
	}
}
